//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { ReactElement, useEffect } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import Modal from '@mui/material/Modal'
import ButtonAction, { ButtonActionProps } from './elements/ButtonAction'
import Paper from '@mui/material/Paper'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import { logOT } from '@shared/loggingShared'

export const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: '0px 16px 50px rgba(48, 49, 51, 0.08), 0px 0px 0.5px #303133',
  p: 5,
}

export interface ButtonModalProps extends Omit<ButtonActionProps, 'children'> {
  title: string
  children: ReactElement
  modalWidth?: number
  modalCloseCallback?: () => void
  icon?: ReactElement
  disableClose?: boolean
}

export default function ButtonModal(props: ButtonModalProps): ReactElement {
  const { variant, title } = props
  const [openModal, setOpenModal] = React.useState(false)
  const theme = useTheme()

  const callBack = React.useCallback(() => {
    logOT.info('modalCloseCallback 2')
    if (props.modalCloseCallback) {
      logOT.info('modalCloseCallback 3')
      props.modalCloseCallback()
    }
  }, [props.modalCloseCallback])

  useEffect(() => {
    const externalHandleModalClose = () => {
      setOpenModal(false)
      callBack()
    }

    window.addEventListener('modalClose', externalHandleModalClose, false)

    return () => {
      window.removeEventListener('modalClose', externalHandleModalClose, false)
    }
  }, [callBack])

  const handleClose = (): void => {
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }
  const calculatedModalStyle = { ...modalStyle, width: props.modalWidth ?? 600 }
  return (
    <>
      {props.icon && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', marginBottom: 2, cursor: 'pointer' }}
          onClick={() => setOpenModal(true)}
        >
          {props.icon}
        </Box>
      )}
      {!props.icon && (
        <ButtonAction
          disabled={props.disabled ?? !!props.isAnotherRoleString}
          isAnotherRoleString={props.isAnotherRoleString}
          isLateString={props.isLateString}
          isEarlyString={props.isEarlyString}
          tooltip={props.tooltip}
          variant={variant}
          onClick={() => setOpenModal(true)}
        >
          {title}
        </ButtonAction>
      )}

      <Modal
        open={openModal}
        data-testid="modal"
        onClose={() => {
          if (props.disableClose) {
            return
          }
          setOpenModal(false)
          callBack()
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Paper sx={calculatedModalStyle}>
          <Typography
            align="left"
            sx={{ display: 'inline', float: 'left', fontWeight: 'bold', marginLeft: 4 }}
            variant={'h4'}
          >
            {title}{' '}
          </Typography>
          {!props.disableClose && (
            <IconButton
              onClick={handleClose}
              sx={{ height: '14px', width: '14px', padding: 0, float: 'right', color: theme.palette.primary.main }}
            >
              <CloseIcon sx={{ width: '12px', height: '12px', marginTop: 0, color: theme.palette.fg.muted }} />
            </IconButton>
          )}
          <Box sx={{ marginTop: '32px', marginLeft: 2 }}>{props.children}</Box>
        </Paper>
      </Modal>
    </>
  )
}
